import React from 'react';

function getPageRange(currentPage, totalPages) {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }
  const range = [1];
  const windowStart = Math.max(2, currentPage - 2);
  const windowEnd = Math.min(totalPages - 1, currentPage + 2);
  if (windowStart > 2) range.push('...');
  for (let i = windowStart; i <= windowEnd; i++) range.push(i);
  if (windowEnd < totalPages - 1) range.push('...');
  range.push(totalPages);
  return range;
}

function Pagination({ pagination, onPageChange, loading }) {
  if (!pagination || pagination.totalPages <= 1) return null;

  const { currentPage, totalPages } = pagination;
  const pages = getPageRange(currentPage, totalPages);
  const isFirst = currentPage === 1;
  const isLast = currentPage === totalPages;

  const mobileNavCls = (disabled) =>
    `w-full h-12 rounded-2xl border text-sm font-semibold transition-colors ${
      disabled
        ? 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-800 opacity-50 cursor-not-allowed'
        : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'
    }`;

  const desktopNavCls = (disabled) =>
    `inline-flex items-center justify-center rounded-2xl border px-4 h-10 text-sm font-semibold transition-colors ${
      disabled
        ? 'border-slate-200 dark:border-slate-700 text-slate-400 dark:text-slate-600 bg-white dark:bg-slate-800 opacity-50 cursor-not-allowed'
        : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'
    }`;

  const pageNumCls = (isActive) =>
    `inline-flex items-center justify-center h-10 w-10 rounded-xl text-sm font-semibold transition-colors ${
      isActive
        ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900'
        : 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 cursor-pointer'
    }`;

  return (
    <div className="mt-6 sm:mt-8 mb-4">
      {loading && (
        <div className="flex justify-center mb-3">
          <svg
            className="animate-spin h-5 w-5 text-slate-400 dark:text-slate-500"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        </div>
      )}

      {/* Mobile: Prev / Page X of Y / Next */}
      <div className="flex flex-col gap-3 md:hidden">
        <button
          onClick={() => !isFirst && onPageChange(currentPage - 1)}
          disabled={isFirst || loading}
          className={mobileNavCls(isFirst || loading)}
        >
          ← Previous
        </button>
        <p className="text-center text-sm font-medium text-slate-500 dark:text-slate-400">
          Page {currentPage} of {totalPages}
        </p>
        <button
          onClick={() => !isLast && onPageChange(currentPage + 1)}
          disabled={isLast || loading}
          className={mobileNavCls(isLast || loading)}
        >
          Next →
        </button>
      </div>

      {/* Desktop: ← Previous  1 2 [3] 4 5 … 12  Next → */}
      <div className="hidden md:flex items-center justify-center gap-2">
        <button
          onClick={() => !isFirst && onPageChange(currentPage - 1)}
          disabled={isFirst || loading}
          className={desktopNavCls(isFirst || loading)}
        >
          ← Previous
        </button>

        {pages.map((page, i) =>
          page === '...' ? (
            <span
              key={`dots-${i}`}
              className="flex h-10 w-8 items-center justify-center text-sm text-slate-400 dark:text-slate-500 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={page}
              onClick={() => !loading && onPageChange(page)}
              disabled={loading}
              className={pageNumCls(page === currentPage)}
            >
              {page}
            </button>
          )
        )}

        <button
          onClick={() => !isLast && onPageChange(currentPage + 1)}
          disabled={isLast || loading}
          className={desktopNavCls(isLast || loading)}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export default Pagination;